import React, { useState } from "react";
import "./about.css";

const servicesData = [
  {
    id: "hr-outsourcing",
    name: "HR Outsourcing",
    value: 35,
    color: "#06b6d4", // Cyan
    icon: "🤝",
    desc: "We excel in HR outsourcing, offering scalable solutions that optimize operational efficiency, human resource management, and compliance."
  },
  {
    id: "it-recruitment",
    name: "IT Recruitment",
    value: 20,
    color: "#6366f1", // Indigo
    icon: "💻",
    desc: "Sourcing top-tier software engineers, system architects, and tech leaders to power your digital initiatives."
  },
  {
    id: "recruitment-services",
    name: "Recruitment Services",
    value: 15,
    color: "#10b981", // Emerald
    icon: "🔍",
    desc: "EduITalent’s experienced recruiters work closely with hiring managers to identify specific requirements and attract the best talent. We use job postings, screening, interviews, reference checks, and offers."
  },
  {
    id: "it-consulting",
    name: "IT Consulting",
    value: 10,
    color: "#f59e0b", // Amber
    icon: "💡",
    desc: "Providing strategic advisory services that align technology infrastructure and software capabilities with long-term business goals."
  },
  {
    id: "executive-search",
    name: "Executive Search",
    value: 10,
    color: "#ec4899", // Pink
    icon: "👑",
    desc: "Identifying and recruiting forward-thinking leaders and executive-level talent (C-suite) to steer organizational growth."
  },
  {
    id: "payroll-services",
    name: "3rd Party Payroll",
    value: 10,
    color: "#8b5cf6", // Purple
    icon: "💳",
    desc: "Our 3rd party payroll services streamline payroll management, compliance, and tax filing, while enhancing accuracy and confidentiality."
  }
];

const About = () => {
  const [activeId, setActiveId] = useState("hr-outsourcing");
  const isStandalone = window.location.pathname === "/about";

  // Donut chart parameters
  const cx = 150;
  const cy = 150;
  const rOuter = 120;
  const rInner = 80;

  // Calculate SVG paths for each slice of the donut chart
  let accumulatedAngle = -Math.PI / 2; // Start from top (12 o'clock)

  const slices = servicesData.map((service) => {
    const percentage = service.value;
    const angleRad = (percentage / 100) * (2 * Math.PI);
    const startAngle = accumulatedAngle;
    const endAngle = accumulatedAngle + angleRad;
    accumulatedAngle = endAngle;

    const isActive = activeId === service.id;

    // Shift the active slice outward along its center angle (bisector)
    let shiftX = 0;
    let shiftY = 0;
    if (isActive) {
      const midAngle = (startAngle + endAngle) / 2;
      const shiftDistance = 8;
      shiftX = shiftDistance * Math.cos(midAngle);
      shiftY = shiftDistance * Math.sin(midAngle);
    }

    // Outer circle coordinate points
    const x1Outer = cx + shiftX + rOuter * Math.cos(startAngle);
    const y1Outer = cy + shiftY + rOuter * Math.sin(startAngle);
    const x2Outer = cx + shiftX + rOuter * Math.cos(endAngle);
    const y2Outer = cy + shiftY + rOuter * Math.sin(endAngle);

    // Inner circle coordinate points
    const x1Inner = cx + shiftX + rInner * Math.cos(endAngle);
    const y1Inner = cy + shiftY + rInner * Math.sin(endAngle);
    const x2Inner = cx + shiftX + rInner * Math.cos(startAngle);
    const y2Inner = cy + shiftY + rInner * Math.sin(startAngle);

    const largeArc = percentage > 50 ? 1 : 0;

    const pathData = `
      M ${x1Outer} ${y1Outer}
      A ${rOuter} ${rOuter} 0 ${largeArc} 1 ${x2Outer} ${y2Outer}
      L ${x1Inner} ${y1Inner}
      A ${rInner} ${rInner} 0 ${largeArc} 0 ${x2Inner} ${y2Inner}
      Z
    `.trim();

    return {
      ...service,
      pathData,
      isActive
    };
  });

  const activeService = servicesData.find((s) => s.id === activeId) || servicesData[0];

  return (
    <section id="about" className={`about-page ${isStandalone ? 'standalone-page' : ''}`}>

      {/* Background Decor */}
      <div className="about-decor-circle about-decor-1"></div>
      <div className="about-decor-circle about-decor-2"></div>

      <div className="container">

        {/* Section 1: Hero & Introduction */}
        <div className="about-hero-grid">
          <div className="about-hero-content">
            <span className="about-badge-label">Who We Are</span>
            <h1 className="about-main-title">Propelling Your Business Forward with Top-Tier Talent</h1>
            <p className="about-hero-lead">
              At EduITalent, we recognize that securing the right talent is pivotal to organizational success.
              We specialize in a diverse array of recruitment and consultancy services, tailored to meet the unique needs of every client.
              Our seasoned recruiters collaborate closely with organizations to pinpoint their specific requirements, delivering customized solutions.
            </p>
            <p className="about-hero-body">
              From entry-level positions to executive roles, we possess the acumen and resources to source top-tier talent.
              Our comprehensive hiring programs are crafted to enhance candidate attraction and retention, ensuring a seamless onboarding experience.
              Whether you seek temporary staffing solutions or a strategic HR partner, EduITalent offers the expertise and support to propel your business forward.
            </p>
          </div>
          <div className="about-hero-images">
            <div className="about-img-frame main-frame">
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=700"
                alt="EduITalent Team Collaboration"
                className="about-img-display"
              />
            </div>
            <div className="about-experience-card">
              <span className="exp-num">15+</span>
              <span className="exp-text">Years of Recruiting Excellence</span>
            </div>
          </div>
        </div>

        {/* Section 2: Interactive Services & Donut Chart */}
        <div className="about-chart-section">
          <div className="chart-section-header">
            <span className="about-badge-label">Our Operations</span>
            <h2 className="about-section-title">Our Services Portfolio</h2>
            <p className="about-section-subtitle">
              Hover over the slices or the list items below to explore our core focus areas and detailed service offerings.
            </p>
          </div>

          <div className="about-chart-grid">

            {/* Chart Area */}
            <div className="chart-visual-container">
              <div className="svg-wrapper">
                <svg width="300" height="300" viewBox="0 0 300 300" className="donut-svg">
                  <g className="donut-slices">
                    {slices.map((slice) => (
                      <path
                        key={slice.id}
                        d={slice.pathData}
                        fill={slice.color}
                        className={`donut-slice ${slice.isActive ? 'active' : ''}`}
                        onMouseEnter={() => setActiveId(slice.id)}
                        style={{
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          cursor: "pointer",
                          opacity: activeId && !slice.isActive ? 0.7 : 1,
                          filter: slice.isActive ? "drop-shadow(0px 8px 16px rgba(0,0,0,0.15))" : "none"
                        }}
                      />
                    ))}
                  </g>
                  {/* Center Label */}
                  <circle cx={cx} cy={cy} r={rInner - 5} fill="#ffffff" />
                  <text
                    x={cx}
                    y={cy - 10}
                    textAnchor="middle"
                    className="donut-center-val"
                    style={{ fontSize: "1.8rem", fontWeight: "800", fill: activeService.color, transition: "fill 0.3s" }}
                  >
                    {activeService.value}%
                  </text>
                  <text
                    x={cx}
                    y={cy + 15}
                    textAnchor="middle"
                    className="donut-center-lbl"
                    style={{ fontSize: "0.85rem", fontWeight: "600", fill: "#4b5563" }}
                  >
                    Focus Allocation
                  </text>
                </svg>
              </div>

              {/* Dynamic Legend */}
              <div className="chart-legend">
                {servicesData.map((service) => (
                  <div
                    key={service.id}
                    className={`legend-item ${activeId === service.id ? 'active' : ''}`}
                    onMouseEnter={() => setActiveId(service.id)}
                  >
                    <span className="legend-bullet" style={{ backgroundColor: service.color }}></span>
                    <span className="legend-name">{service.name}</span>
                    <span className="legend-pct">{service.value}%</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Info Card Area */}
            <div className="chart-info-card" style={{ borderColor: activeService.color }}>
              <div className="info-card-icon-wrap" style={{ backgroundColor: `${activeService.color}15`, color: activeService.color }}>
                <span className="info-card-icon">{activeService.icon}</span>
              </div>
              <h3 className="info-card-title">{activeService.name}</h3>
              <div className="info-card-badge" style={{ backgroundColor: `${activeService.color}15`, color: activeService.color }}>
                {activeService.value}% Focus Area
              </div>
              <p className="info-card-desc">
                {activeService.desc}
              </p>
            </div>

          </div>
        </div>

        {/* Section 3: Philosophy & Strategic Partnership */}
        <div className="about-philosophy-section">
          <div className="about-philosophy-grid">
            <div className="philosophy-image">
              <img
                src="https://images.unsplash.com/photo-1531482615713-2afd69097998?w=600"
                alt="EduITalent Values & Collaboration"
                className="philosophy-img"
              />
              <div className="philosophy-quote-overlay">
                <p>“We are not just recruiters; we are strategic partners committed to fostering long-term success for your organization.”</p>
              </div>
            </div>

            <div className="philosophy-content">
              <span className="about-badge-label">Our Values</span>
              <h2 className="about-section-title">Driving Growth Through Transformative Talent</h2>

              <p className="about-p">
                At EduITalent, we believe in the transformative power of exceptional talent to drive organizational success.
                We specialize in a comprehensive range of recruitment and consultancy services tailored to meet the diverse needs of modern businesses.
                Our dedicated team of recruiters brings years of expertise in sourcing top-tier talent, from entry-level positions to executive roles.
                We collaborate closely with our clients to understand their unique requirements and deliver customized solutions that align with their strategic goals.
              </p>

              <p className="about-p">
                Our recruitment empanelment services, also known as staffing solutions, ensure that your workforce is agile and responsive to market demands.
                We excel in HR outsourcing, offering scalable solutions that optimize operational efficiency and compliance.
                Additionally, our 3rd party payroll services streamline payroll management, enhancing accuracy and confidentiality.
              </p>

              <p className="about-p">
                We prioritize creating a positive candidate experience and ensuring a seamless onboarding process.
                Whether you need short-term staffing support or a trusted advisor for strategic HR decisions, our comprehensive suite of services is designed to meet your evolving needs.
              </p>
            </div>
          </div>
        </div>

        {/* Section 4: Call to Action (Empanelment & Contact) */}
        <div className="about-cta-card">
          <div className="cta-content">
            <h2>Empower Your Business to Thrive</h2>
            <p>
              Join leading companies who trust EduITalent for innovative solutions and unparalleled expertise in talent acquisition and management.
              Contact us today to explore our recruitment empanelment (staffing solutions), HR outsourcing, and 3rd party payroll services.
            </p>
            <div className="cta-buttons">
              <a href="/contact" className="about-cta-btn primary-btn">Contact Us Today</a>
              <a href="/services" className="about-cta-btn secondary-btn">Explore Services</a>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default About;
